<template>
	<v-tooltip :text="tooltip" :disabled="!tooltip">
		<template #activator="{ props }">
			<!-- Both defaults are spread first, so anything the caller passes wins. -->
			<v-btn rounded="sm"
				v-bind="{ variant: DEFAULT_VARIANT, color: defaultColor, ...props, ...$attrs }"
				:class="[ $attrs.class, 'm-btn']">
				<template v-for="(_, slotName) in $slots" #[slotName]>
					<slot :name="slotName" />
				</template>
			</v-btn>
		</template>
	</v-tooltip>
</template>

<script setup lang="ts">
// Registered locally by each consumer as `MBtn` and used as <m-btn>. The old
// `name: "m-btn"` option played no part in that resolution, so dropping it with
// the Options API shell changes nothing.
//
// DO NOT add `defineEmits` here — specifically not for `click`.
//
// This component forwards every listener to the inner v-btn through
// `v-bind="$attrs"`. Vue *removes* a declared emit from `$attrs`, so declaring
// `click` silently strips the parent's handler before the v-bind runs and every
// `<m-btn @click="…">` in the app stops responding — 47 call sites, no error, no
// type failure, no test failure. That is exactly what happened when this file
// was converted from the Options API, which had no `emits` option for the same
// reason. `src/tests/unit/baseButton.test.ts` guards it now.
//
// If this component ever needs to emit an event of its own, give it a name the
// parent does not already bind as a native listener.
defineOptions({ inheritAttrs: false })

withDefaults(defineProps<{ tooltip?: string }>(), { tooltip: '' })

import { computed, useAttrs } from 'vue'

const attrs = useAttrs()

/**
 * `elevated` is this app's house style for a button, and it also has to be
 * stated: `VCardActions` provides `variant: 'text'` to any nested `VBtn`, so
 * without this every button inside a card's actions silently becomes a flat
 * text button.
 */
const DEFAULT_VARIANT = 'elevated'

/**
 * Only background-painting variants get a default colour.
 *
 * `text`, `plain`, `outlined` and `tonal` paint no background and are *designed*
 * to inherit their colour from context — that is how the app-bar icon buttons
 * pick up the bar's foreground. Forcing a colour on those makes Vuetify emit
 * `.text-surface`, i.e. white, which renders them invisible on a white app bar.
 *
 * `elevated` and `flat` are the opposite case: they paint a background, and the
 * only rule Vuetify leaves setting their text colour is a `color-mix()` over
 * `--v-high-emphasis-opacity` that does not resolve. With nothing else to go on
 * they inherit instead — white-on-white on a coloured container. Naming the
 * colour makes Vuetify emit `.bg-surface`, which sets background and text with
 * plain `rgb()` values.
 *
 * Same appearance as before in both cases; neither now depends on inheritance.
 */
const PAINTS_BACKGROUND = new Set(['elevated', 'flat'])

const defaultColor = computed(() => {
	const variant = (attrs.variant as string) ?? DEFAULT_VARIANT
	return PAINTS_BACKGROUND.has(variant) ? 'surface' : undefined
})
</script>

<style scoped lang="scss">
.m-btn {
	&.v-btn--disabled, &:disabled {
		opacity: 0.5;
		cursor: not-allowed !important;
	}
}
</style>
